import React, { useEffect, useMemo, useRef, useState } from "react";
import { activityByKey, defaultPaceLbsPerWeek, deriveGoalDirection, fullResultFromDraft } from "./calc";
import { ScreenHeaderRightProvider } from "./components";
import { DesktopFlow } from "./DesktopFlow";
import type {
  ActivityKey,
  AuthChoice,
  GoalDirection,
  HeightUnit,
  ImportFromHealthOutcome,
  OnboardingDraft,
  OnboardingResult,
  PacePreset,
  Sex,
  WeightUnit,
} from "./types";
import { ActivityScreen } from "./screens/ActivityScreen";
import { AccountScreen } from "./screens/AccountScreen";
import { DOBScreen } from "./screens/DOBScreen";
import { GoalScreen } from "./screens/GoalScreen";
import { HealthKitConnectScreen } from "./screens/HealthKitConnectScreen";
import { HeightScreen } from "./screens/HeightScreen";
import { PaceScreen } from "./screens/PaceScreen";
import { PlanRevealScreen } from "./screens/PlanRevealScreen";
import { SexScreen } from "./screens/SexScreen";
import { TargetScreen } from "./screens/TargetScreen";
import { WeightScreen } from "./screens/WeightScreen";
import { WelcomeScreen } from "./screens/WelcomeScreen";

type Step =
  | "welcome"
  | "goal"
  | "sex"
  | "dob"
  | "height"
  | "healthkit"
  | "weight"
  | "activity"
  | "target"
  | "pace"
  | "plan"
  | "account";

const FLOW: Step[] = [
  "welcome",
  "healthkit",
  "goal",
  "sex",
  "dob",
  "height",
  "weight",
  "activity",
  "target",
  "pace",
  "plan",
  "account",
];

// Base progress sequence (welcome doesn't count, plan + account are end states).
// The "healthkit" step is prepended at render time for iOS users who haven't
// already connected — see `progressSteps` in the component.
const BASE_PROGRESS_STEPS: Step[] = ["goal", "sex", "dob", "height", "weight", "activity", "target", "pace"];

function initialDraft(): OnboardingDraft {
  // Sensible defaults that produce a working plan even if a user blasts through.
  return {
    goalDirection: null,
    sex: null,
    birthday: "1996-06-15",
    heightIn: 70,
    currentWeightLbs: 180,
    targetWeightLbs: 170,
    targetTouched: false,
    unitsHeight: "in",
    unitsWeight: "lb",
    activityKey: "light",
    activityFactor: 1.3,
    paceLbsPerWeekAbs: 0.75,
    pacePreset: "moderate",
  };
}

// Smart default delta from current weight, by goal.
const DEFAULT_LOSE_DELTA_LBS = 10;
const DEFAULT_GAIN_DELTA_LBS = 8;

export type OnboardingFlowV2Props = {
  isMobile: boolean;
  isNativeIOS: boolean;
  /** True when there's already a Supabase session for the user (and we're
   * not in demo mode). Short-circuits the final auth step at plan reveal —
   * an already-authed user shouldn't see a redundant sign-in screen. */
  isAuthenticated: boolean;
  initialStep?: Step;
  onComplete: (result: OnboardingResult, choice: AuthChoice) => Promise<void> | void;
  onSignIn?: () => void;
  /** Invoked when the user taps back from the very first step. Lets the
   * parent return to the landing page instead of falling back to the
   * V2-internal WelcomeScreen (which is a separate "intro" UI and not
   * what the user expects to see when backing out). */
  onExit?: () => void;
  onImportFromHealth?: () => Promise<ImportFromHealthOutcome>;
  /** Current HealthKit connection state from the parent. Drives whether we
   *  insert the dedicated "Connect Apple Health" step and whether the Weight
   *  step auto-pre-fills from the latest HK sample. Frozen at first render
   *  so we don't reshape the progress bar mid-flow. */
  healthKitEnabled?: boolean;
  onAuthApple?: () => Promise<void> | void;
  onAuthGoogle?: () => Promise<void> | void;
  onAuthMagic?: (email: string) => Promise<void> | void;
};

export function OnboardingFlowV2(props: OnboardingFlowV2Props): React.ReactElement {
  const { isMobile, isNativeIOS, isAuthenticated, initialStep = "welcome", onComplete, onSignIn, onExit, onImportFromHealth, healthKitEnabled, onAuthApple, onAuthGoogle, onAuthMagic } = props;

  // Decide once, at mount, whether to insert the dedicated HealthKit step.
  // Freezing this prevents the progress bar from reshaping mid-flow when the
  // user connects on that step (which flips `healthKitEnabled` to true).
  const includeHealthKitStepRef = useRef<boolean>(!!isNativeIOS && !healthKitEnabled);
  const includeHealthKitStep = includeHealthKitStepRef.current;

  // When HK is on the menu it leads the flow — the value pitch lands before
  // any data-collection questions. If the caller defaulted to "goal" (the
  // common entry from WeightTracker) and HK is included, start on HK instead.
  const [step, setStep] = useState<Step>(() => {
    if (includeHealthKitStepRef.current && (initialStep === "goal" || initialStep === "welcome")) {
      return "healthkit";
    }
    return initialStep;
  });
  const [draft, setDraft] = useState<OnboardingDraft>(() => initialDraft());

  // Track whether we've already auto-imported into the Weight step so a back
  // navigation doesn't silently re-pull from HealthKit.
  const hasAutoImportedWeightRef = useRef<boolean>(false);

  // Keep target weight + pace defaults in sync with the user's goal + current
  // weight, until the user actively scrubs the target ruler. Once they touch
  // it (`targetTouched=true`) we leave it alone so subsequent edits to the
  // current weight don't yank the target out from under them.
  useEffect(() => {
    setDraft((d) => {
      if (!d.goalDirection) return d;
      if (d.targetTouched) return d;
      const next = { ...d };
      if (d.goalDirection === "maintain") {
        next.targetWeightLbs = d.currentWeightLbs;
      } else if (d.goalDirection === "lose") {
        next.targetWeightLbs = Math.max(d.currentWeightLbs - DEFAULT_LOSE_DELTA_LBS, 80);
      } else if (d.goalDirection === "gain") {
        next.targetWeightLbs = Math.min(d.currentWeightLbs + DEFAULT_GAIN_DELTA_LBS, 500);
      }
      next.paceLbsPerWeekAbs = defaultPaceLbsPerWeek(d.goalDirection, next.currentWeightLbs, next.targetWeightLbs);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.goalDirection, draft.currentWeightLbs]);

  const progressSteps: Step[] = useMemo(() => {
    if (!includeHealthKitStep) return BASE_PROGRESS_STEPS;
    return ["healthkit", ...BASE_PROGRESS_STEPS];
  }, [includeHealthKitStep]);
  const stepIndex = (s: Step) => progressSteps.indexOf(s);
  const progress = useMemo(() => {
    const idx = stepIndex(step);
    return { current: idx + 1, total: progressSteps.length };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, progressSteps]);

  // Tracks whether the next mount of WeightScreen should auto-trigger the HK
  // import. Set on each transition *into* the weight step; the screen consumes
  // it on mount and the ref above guarantees we fire at most once per session.
  const [autoImportWeightOnMount, setAutoImportWeightOnMount] = useState(false);

  function captureWeightAutoImport(): void {
    if (isNativeIOS && healthKitEnabled && !hasAutoImportedWeightRef.current) {
      hasAutoImportedWeightRef.current = true;
      setAutoImportWeightOnMount(true);
    } else {
      setAutoImportWeightOnMount(false);
    }
  }

  function go(next: Step): void {
    if (next === "weight") captureWeightAutoImport();
    setStep(next);
  }

  function backTo(prev: Step): void {
    if (prev === "weight") captureWeightAutoImport();
    setStep(prev);
  }

  function setField<K extends keyof OnboardingDraft>(key: K, value: OnboardingDraft[K]): void {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  // Effective goal direction is always inferred from the actual delta between
  // current and target weight. This keeps the projection chart, daily-intake
  // math, and saved sprint kind all in sync with what the user is *actually*
  // setting up — independent of whatever direction they originally picked on
  // the Goal screen.  The original choice still lives on `draft.goalDirection`
  // and is shown as a hint on the Target screen ("you picked Lose, looks like
  // a gain") so we can offer them a one-tap switch.
  const goal: GoalDirection = deriveGoalDirection(draft.currentWeightLbs, draft.targetWeightLbs);
  const storedGoal: GoalDirection | null = draft.goalDirection;

  async function buildAndComplete(choice: AuthChoice): Promise<void> {
    const summary = fullResultFromDraft(draft);
    const result: OnboardingResult = {
      draft,
      bmr: Math.round(summary.bmr),
      tdee: Math.round(summary.tdee),
      dailyTargetCalories: summary.dailyTarget,
      targetDateYmd: summary.endYmd,
      sprintName: summary.sprintName,
    };
    await onComplete(result, choice);
  }

  // Branch to the desktop layout (60/40 split + live preview) on wide screens.
  // Native iOS always uses the mobile flow regardless of viewport.
  if (!isMobile && !isNativeIOS) {
    return (
      <DesktopFlow
        draft={draft}
        setDraft={setDraft}
        goal={goal}
        storedGoal={storedGoal}
        isAuthenticated={isAuthenticated}
        onComplete={buildAndComplete}
        onSignIn={onSignIn}
      />
    );
  }

  // "Sign in" escape hatch in the mobile header — injected into each step's
  // ScreenShell via the ScreenHeaderRight context (so the step bar shrinks
  // to make room rather than getting covered by a floating overlay).
  // Tap opens the existing SignInModal; cancelling returns to the same step.
  // Suppressed on AccountScreen (already on auth) and Welcome (has its own).
  const showSignIn = !!onSignIn && !isAuthenticated && step !== "account" && step !== "welcome";
  const signInNode: React.ReactNode = showSignIn ? (
    <button
      type="button"
      onClick={onSignIn}
      style={{
        background: "transparent",
        border: "none",
        color: "rgba(255,255,255,0.78)",
        fontSize: "0.84rem",
        fontWeight: 600,
        cursor: "pointer",
        padding: "4px 6px",
        textDecoration: "underline",
        textUnderlineOffset: 3,
        textDecorationColor: "rgba(255,255,255,0.28)",
        whiteSpace: "nowrap",
      }}
    >
      Sign in
    </button>
  ) : null;

  const withOverlay = (el: React.ReactElement): React.ReactElement => (
    <ScreenHeaderRightProvider value={signInNode}>{el}</ScreenHeaderRightProvider>
  );

  if (step === "welcome") {
    return (
      <WelcomeScreen
        isMobile={isMobile}
        onStart={() => go(includeHealthKitStep ? "healthkit" : "goal")}
        onSignIn={() => {
          if (onSignIn) onSignIn();
          else go("account");
        }}
      />
    );
  }

  if (step === "healthkit") {
    return withOverlay(
      <HealthKitConnectScreen
        step={progress.current}
        total={progress.total}
        isMobile={isMobile}
        // First step in the flow — back exits to the landing page.
        onBack={onExit ? () => onExit() : null}
        onSkip={() => go("goal")}
        onConnect={async () => {
          if (!onImportFromHealth) return { kind: "error" } as const;
          const result = await onImportFromHealth();
          if (result.kind === "imported" && result.weightLbs > 0) {
            setField("currentWeightLbs", result.weightLbs);
          }
          return result;
        }}
      />
    );
  }

  if (step === "goal") {
    return withOverlay(
      <GoalScreen
        step={progress.current}
        total={progress.total}
        value={draft.goalDirection}
        onChange={(v) => setField("goalDirection", v)}
        onNext={() => go("sex")}
        // Back: HK step if it's part of the flow, otherwise exit to landing.
        // (We don't fall back to the V2 WelcomeScreen — that's a separate
        // intro UI and the user shouldn't see two "welcomes".)
        onBack={includeHealthKitStep ? () => backTo("healthkit") : (onExit ? () => onExit() : null)}
        isMobile={isMobile}
      />
    );
  }

  if (step === "sex") {
    return withOverlay(
      <SexScreen
        step={progress.current}
        total={progress.total}
        value={draft.sex}
        onChange={(v: Sex) => setField("sex", v)}
        onNext={() => go("dob")}
        onBack={() => backTo("goal")}
        isMobile={isMobile}
      />
    );
  }

  if (step === "dob") {
    return withOverlay(
      <DOBScreen
        step={progress.current}
        total={progress.total}
        value={draft.birthday}
        onChange={(v) => setField("birthday", v)}
        onNext={() => go("height")}
        onBack={() => backTo("sex")}
        isMobile={isMobile}
      />
    );
  }

  if (step === "height") {
    return withOverlay(
      <HeightScreen
        step={progress.current}
        total={progress.total}
        heightIn={draft.heightIn}
        unit={draft.unitsHeight}
        onHeightChange={(v) => setField("heightIn", v)}
        onUnitChange={(u: HeightUnit) => setField("unitsHeight", u)}
        onNext={() => go("weight")}
        onBack={() => backTo("dob")}
        isMobile={isMobile}
      />
    );
  }

  if (step === "weight") {
    return withOverlay(
      <WeightScreen
        step={progress.current}
        total={progress.total}
        currentWeightLbs={draft.currentWeightLbs}
        unit={draft.unitsWeight}
        onWeightChange={(lbs) => setField("currentWeightLbs", lbs)}
        onUnitChange={(u: WeightUnit) => setField("unitsWeight", u)}
        onNext={() => go("activity")}
        onBack={() => backTo("height")}
        isMobile={isMobile}
        isNativeIOS={isNativeIOS}
        autoImportOnMount={autoImportWeightOnMount}
        onImportFromHealth={onImportFromHealth ? async () => {
          const result = await onImportFromHealth();
          if (result.kind === "imported" && result.weightLbs > 0) {
            setField("currentWeightLbs", result.weightLbs);
          }
          return result;
        } : undefined}
      />
    );
  }

  if (step === "activity") {
    return withOverlay(
      <ActivityScreen
        step={progress.current}
        total={progress.total}
        value={draft.activityKey}
        onChange={(key: ActivityKey) => {
          const fac = activityByKey(key).factor;
          setDraft((d) => ({ ...d, activityKey: key, activityFactor: fac }));
        }}
        onNext={() => go("target")}
        onBack={() => backTo("weight")}
        isMobile={isMobile}
      />
    );
  }

  if (step === "target") {
    return withOverlay(
      <TargetScreen
        step={progress.current}
        total={progress.total}
        goal={goal}
        storedGoal={storedGoal}
        currentLbs={draft.currentWeightLbs}
        targetLbs={draft.targetWeightLbs}
        unit={draft.unitsWeight}
        onUnitChange={(u: WeightUnit) => setField("unitsWeight", u)}
        onTargetChange={(lbs) => {
          setDraft((d) => {
            // Use inferred direction so pace tracks the actual delta even if
            // the stored goal is "maintain" or doesn't match the new target.
            const delta = lbs - d.currentWeightLbs;
            const inferred: GoalDirection = Math.abs(delta) < 0.5 ? "maintain" : delta < 0 ? "lose" : "gain";
            return {
              ...d,
              targetWeightLbs: lbs,
              targetTouched: true,
              paceLbsPerWeekAbs: defaultPaceLbsPerWeek(inferred, d.currentWeightLbs, lbs),
            };
          });
        }}
        onGoalChange={(g) => {
          setDraft((d) => ({
            ...d,
            goalDirection: g,
            paceLbsPerWeekAbs: defaultPaceLbsPerWeek(g, d.currentWeightLbs, d.targetWeightLbs),
          }));
        }}
        onNext={() => go("pace")}
        onBack={() => backTo("activity")}
        isMobile={isMobile}
      />
    );
  }

  if (step === "pace") {
    return withOverlay(
      <PaceScreen
        step={progress.current}
        total={progress.total}
        draft={draft}
        goal={goal}
        onPaceChange={(v: number, preset?: PacePreset) => {
          setDraft((d) => ({
            ...d,
            paceLbsPerWeekAbs: v,
            pacePreset: preset ?? d.pacePreset,
          }));
        }}
        onNext={() => go("plan")}
        onBack={() => backTo("target")}
        isMobile={isMobile}
      />
    );
  }

  if (step === "plan") {
    return withOverlay(
      <PlanRevealScreen
        step={progressSteps.length}
        total={progressSteps.length}
        draft={draft}
        goal={goal}
        // Already-authed users: short-circuit. The Account step would be
        // a redundant sign-in screen since their session is already valid.
        onSavePlan={isAuthenticated
          ? () => buildAndComplete({ kind: "already-authed" })
          : () => go("account")}
        onEdit={() => backTo("target")}
        onBack={() => backTo("pace")}
        isMobile={isMobile}
      />
    );
  }

  if (step === "account") {
    return (
      <AccountScreen
        step={progressSteps.length}
        total={progressSteps.length}
        isNativeIOS={isNativeIOS}
        isMobile={isMobile}
        onApple={async () => {
          if (onAuthApple) await onAuthApple();
          await buildAndComplete({ kind: "apple" });
        }}
        onGoogle={async () => {
          if (onAuthGoogle) await onAuthGoogle();
          await buildAndComplete({ kind: "google" });
        }}
        onMagic={async (email) => {
          if (onAuthMagic) await onAuthMagic(email);
          await buildAndComplete({ kind: "magic", email });
        }}
        onSkip={() => buildAndComplete({ kind: "skip" })}
        onBack={() => backTo("plan")}
      />
    );
  }

  return <span />;
}
